import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import emailjs from "emailjs-com";
import {
  ArrowRight,
  BadgeCheck,
  Banknote,
  BarChart3,
  Boxes,
  BriefcaseBusiness,
  Building2,
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  Landmark,
  LifeBuoy,
  LockKeyhole,
  Mail,
  MessageCircle,
  Phone,
  PlayCircle,
  ReceiptText,
  Search,
  ShieldCheck,
  UsersRound,
  WalletCards,
} from "lucide-react";

import Footer from "./Footer";
import "./Support.css";

const fadeInUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};

const categories = [
  {
    key: "getting-started",
    title: "Getting Started",
    desc: "Company setup, first login, profile setup, and your first steps.",
    icon: LifeBuoy,
  },
  {
    key: "sales",
    title: "Sales & Invoicing",
    desc: "Invoices, estimates, receipts, customers, and payment tracking.",
    icon: ReceiptText,
  },
  {
    key: "accounting",
    title: "Accounting & Reports",
    desc: "Chart of accounts, journals, ledger, trial balance, and reports.",
    icon: Landmark,
  },
  {
    key: "inventory",
    title: "Inventory",
    desc: "Items, warehouses, stock movement, valuation, and ageing stock.",
    icon: Boxes,
  },
  {
    key: "payroll",
    title: "Payroll",
    desc: "Employees, payroll runs, payslips, approvals, and posting.",
    icon: UsersRound,
  },
  {
    key: "roles",
    title: "Team & Roles",
    desc: "Super Admin, Accountant, HR, Marketing, and restricted access.",
    icon: LockKeyhole,
  },
  {
    key: "banking",
    title: "Banking & Reconciliation",
    desc: "Bank transactions, cash movement, and reconciliation support.",
    icon: Banknote,
  },
  {
    key: "crm",
    title: "CRM & Scheduling",
    desc: "Customers, follow-ups, bookings, tasks, and scheduling workflows.",
    icon: CalendarCheck,
  },
];

const articles = [
  { title: "Set up your company profile", cat: "getting-started" },
  { title: "Create your first invoice", cat: "sales" },
  { title: "Convert an estimate to an invoice", cat: "sales" },
  { title: "Record customer payments", cat: "sales" },
  { title: "Configure VAT and WHT", cat: "accounting" },
  { title: "View your trial balance", cat: "accounting" },
  { title: "Generate income statement and balance sheet", cat: "accounting" },
  { title: "Add inventory items and warehouses", cat: "inventory" },
  { title: "Post payroll to journal", cat: "payroll" },
  { title: "Invite users and assign roles", cat: "roles" },
  { title: "Reconcile bank transactions", cat: "banking" },
  { title: "Use CRM follow-ups and bookings", cat: "crm" },
];

const videoGroups = [
  {
    key: "getting-started",
    title: "Getting Started",
    icon: LifeBuoy,
    videos: [
      {
        title: "Create your account and company",
        videoId: "6cKDKi7mACQ",
      },
      { title: "Set up company information", videoId: "nBCv_peTJYE" },
      { title: "How To Set Up Team Access", videoId: "WvXNqV3GODk" },
      { title: "Choose the right plan", videoId: "pNwlyCQge7M" },
    ],
  },
  {
    key: "sales",
    title: "Sales, Invoices & Receipts",
    icon: ReceiptText,
    videos: [
      { title: "Create your first invoice", videoId: "WUvMoy_x6OY" },
      { title: "Create and send an estimate", videoId: "WUvMoy_x6OY" },
      { title: "Convert estimate to invoice", videoId: "WUvMoy_x6OY" },
      { title: "Record customer payment", videoId: "WUvMoy_x6OY" },
      { title: "Track overdue invoices", videoId: "WUvMoy_x6OY" },
    ],
  },
  {
    key: "accounting",
    title: "Accounting & Reports",
    icon: Landmark,
    videos: [
      { title: "Understand chart of accounts", videoId: "iskDyJL73KQ" },
      { title: "Create journal entries", videoId: "iskDyJL73KQ" },
      { title: "View general ledger", videoId: "iskDyJL73KQ" },
      { title: "Generate trial balance", videoId: "iskDyJL73KQ" },
      { title: "Generate financial reports", videoId: "iskDyJL73KQ" },
    ],
  },
  {
    key: "inventory",
    title: "Inventory Management",
    icon: Boxes,
    videos: [
      { title: "Add inventory items", videoId: "m6dA5Bi0N30" },
      { title: "Create warehouses", videoId: "m6dA5Bi0N30" },
      { title: "Record stock movement", videoId: "m6dA5Bi0N30" },
      { title: "Check stock-on-hand", videoId: "m6dA5Bi0N30" },
      { title: "View inventory valuation", videoId: "m6dA5Bi0N30" },
    ],
  },
  {
    key: "payroll",
    title: "Employee Payroll",
    icon: UsersRound,
    videos: [
      { title: "Add employees", videoId: "3WUp4MazEiY" },
      { title: "Run payroll", videoId: "3WUp4MazEiY" },
      { title: "Approve payroll", videoId: "3WUp4MazEiY" },
      { title: "Generate payslips", videoId: "3WUp4MazEiY" },
      { title: "Post payroll to journal", videoId: "3WUp4MazEiY" },
    ],
  },
  {
    key: "team",
    title: "Team, Roles & Permissions",
    icon: LockKeyhole,
    videos: [
      { title: "Invite a team member", videoId: "WvXNqV3GODk" },
      { title: "Assign user roles", videoId: "WvXNqV3GODk" },
      { title: "Super Admin overview", videoId: "WvXNqV3GODk" },
      { title: "Accountant access overview", videoId: "WvXNqV3GODk" },
      { title: "HR and Marketing user access", videoId: "WvXNqV3GODk" },
    ],
  },
  {
    key: "operations",
    title: "CRM, Budgeting, Booking & Tasks",
    icon: BriefcaseBusiness,
    videos: [
      { title: "Manage CRM records", videoId: "Ov-S2z5GGQw" },
      { title: "Track follow-ups", videoId: "Ov-S2z5GGQw" },
      { title: "Create a budget", videoId: "Ov-S2z5GGQw" },
      { title: "Use booking and scheduling", videoId: "Ov-S2z5GGQw" },
      { title: "Assign and track tasks", videoId: "Ov-S2z5GGQw" },
    ],
  },
  {
    key: "assets-banking",
    title: "Fixed Assets, Banking & Reconciliation",
    icon: Building2,
    videos: [
      { title: "Add fixed assets", videoId: "rYOVAGyudps" },
      { title: "Run depreciation", videoId: "rYOVAGyudps" },
      { title: "Record bank transactions", videoId: "rYOVAGyudps" },
      { title: "Reconcile bank records", videoId: "rYOVAGyudps" },
    ],
  },
];

const faqs = [
  {
    q: "What support channels are available?",
    a: "You can contact support by ticket, email, phone, or the in-app chat when available.",
  },
  {
    q: "How do I find the right help video?",
    a: "Use the video category buttons. Videos are grouped by feature, such as invoices, payroll, inventory, reports, and team roles.",
  },
  {
    q: "Can I get help setting up my company?",
    a: "Yes. You can submit a ticket and our team can guide you through company setup, users, invoices, payroll, and reports.",
  },
  {
    q: "Can I export my data?",
    a: "Yes. KostPadi supports export-friendly reports and documents such as PDF, Excel, and CSV where available.",
  },
];

export default function Support() {
  const [query, setQuery] = useState("");
  const [activeVideoGroup, setActiveVideoGroup] = useState(videoGroups[0].key);
  const [videoPage, setVideoPage] = useState(1);
  const [ticket, setTicket] = useState({
    name: "",
    email: "",
    subject: "",
    type: "Billing",
    priority: "Normal",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  const pageSize = 4;

  const filteredArticles = useMemo(() => {
    if (!query.trim()) return articles;

    return articles.filter((article) =>
      article.title.toLowerCase().includes(query.toLowerCase()),
    );
  }, [query]);

  const activeGroup = videoGroups.find(
    (group) => group.key === activeVideoGroup,
  );
  const totalPages = Math.ceil(activeGroup.videos.length / pageSize);
  const paginatedVideos = activeGroup.videos.slice(
    (videoPage - 1) * pageSize,
    videoPage * pageSize,
  );

  const handleVideoGroupChange = (key) => {
    setActiveVideoGroup(key);
    setVideoPage(1);
  };

  const onTicketChange = (e) =>
    setTicket((t) => ({ ...t, [e.target.name]: e.target.value }));

  const submitTicket = async (e) => {
    e.preventDefault();
    setSending(true);
    setSent(null);

    try {
      await emailjs.send(
        "YOUR_SERVICE_ID",
        "YOUR_TEMPLATE_ID",
        {
          from_name: ticket.name,
          reply_to: ticket.email,
          subject: `[${ticket.type}] ${ticket.subject} (${ticket.priority})`,
          message: ticket.message,
        },
        "YOUR_PUBLIC_KEY",
      );

      setSent("ok");
      setTicket({
        name: "",
        email: "",
        subject: "",
        type: "Billing",
        priority: "Normal",
        message: "",
      });
    } catch (err) {
      console.error(err);
      setSent("err");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="support-root">
      <div className="support-status">
        <div className="kp-container support-status-inner">
          <span className="support-dot" />
          <strong>All systems operational</strong>
          <a href="/status">View status</a>
        </div>
      </div>

      <header className="kp-container support-hero" id="top">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="support-hero-inner"
        >
          <motion.span variants={fadeInUp} className="support-tag">
            Support & Help Centre
          </motion.span>

          <motion.h1 variants={fadeInUp}>
            Get help with KostPadi faster.
          </motion.h1>

          <motion.p variants={fadeInUp} className="support-lead">
            Search helpful articles, watch feature-based videos, or send a
            ticket to our team. Everything is organised by the way you use
            KostPadi.
          </motion.p>

          <motion.form
            variants={fadeInUp}
            className="support-search"
            onSubmit={(e) => e.preventDefault()}
          >
            <Search size={20} />
            <input
              type="text"
              placeholder="Search e.g. invoice, payroll, inventory, user roles"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search support articles"
            />
            <button className="support-btn support-btn-primary" type="submit">
              Search
            </button>
          </motion.form>

          {query && (
            <motion.div variants={fadeInUp} className="support-search-results">
              {filteredArticles.length ? (
                filteredArticles.map((article) => (
                  <a key={article.title} href="/support">
                    {article.title}
                  </a>
                ))
              ) : (
                <p>No results found. Try another keyword.</p>
              )}
            </motion.div>
          )}
        </motion.div>
      </header>

      <main>
        <section className="kp-container support-section">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="support-contact-grid"
          >
            <SupportContactCard
              icon={MessageCircle}
              title="Live chat"
              badge="Beta"
              text="Chat with us during support hours when live chat is available."
              actionText="Open chat"
              href="#ticket"
            />

            <SupportContactCard
              icon={Mail}
              title="Email support"
              text="Send us a message and we’ll reply as soon as possible."
              actionText="support@kostpadi.com"
              href="mailto:support@kostpadi.com"
            />

            <SupportContactCard
              icon={Phone}
              title="Phone support"
              text="Speak with someone during local business hours."
              actionText="+234 (903) 198 6790"
              href="tel:+2349031986790"
            />
          </motion.div>
        </section>

        <section className="support-section support-section-alt">
          <div className="kp-container">
            <div className="support-section-head">
              <span className="support-mini-tag">Browse by feature</span>
              <h2>Find help based on what you are trying to do.</h2>
              <p>
                Whether you are creating invoices, running payroll, managing
                inventory, or inviting users, start from the feature category
                below.
              </p>
            </div>

            <div className="support-category-grid">
              {categories.map(({ icon: Icon, title, desc, key }) => (
                <a href="#videos" className="support-category-card" key={key}>
                  <div className="support-icon-wrap">
                    <Icon size={22} />
                  </div>
                  <h3>{title}</h3>
                  <p>{desc}</p>
                  <span>
                    View help <ArrowRight size={16} />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section id="videos" className="kp-container support-section">
          <div className="support-section-head">
            <span className="support-mini-tag">Video tutorials</span>
            <h2>Watch help videos by KostPadi feature.</h2>
            <p>
              Select a category and use the pagination controls to move through
              the available tutorials.
            </p>
          </div>

          <div className="support-video-tabs">
            {videoGroups.map(({ key, title, icon: Icon }) => (
              <button
                key={key}
                type="button"
                className={activeVideoGroup === key ? "active" : ""}
                onClick={() => handleVideoGroupChange(key)}
              >
                <Icon size={17} />
                {title}
              </button>
            ))}
          </div>

          <div className="support-video-panel">
            <div className="support-video-panel-head">
              <div>
                <h3>{activeGroup.title}</h3>
                <p>
                  Showing {paginatedVideos.length} of{" "}
                  {activeGroup.videos.length} videos.
                </p>
              </div>

              <div className="support-video-pagination">
                <button
                  type="button"
                  disabled={videoPage === 1}
                  onClick={() => setVideoPage((page) => page - 1)}
                >
                  <ChevronLeft size={18} />
                </button>
                <span>
                  Page {videoPage} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={videoPage === totalPages}
                  onClick={() => setVideoPage((page) => page + 1)}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>

            <motion.div
              key={`${activeVideoGroup}-${videoPage}`}
              variants={stagger}
              initial="hidden"
              animate="show"
              className="support-video-grid"
            >
              {paginatedVideos.map((video) => (
                <motion.div variants={fadeInUp} key={video.title}>
                  <VideoCard title={video.title} videoId={video.videoId} />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="support-section support-section-alt">
          <div className="kp-container support-guides-wrap">
            <div className="support-section-head">
              <span className="support-mini-tag">Popular guides</span>
              <h2>Common things users ask for.</h2>
            </div>

            <div className="support-guide-grid">
              {articles.map((article) => (
                <a
                  href="/support"
                  className="support-guide-card"
                  key={article.title}
                >
                  <PlayCircle size={18} />
                  <span>{article.title}</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section id="ticket" className="kp-container support-section">
          <div className="support-ticket-wrap">
            <div className="support-ticket-copy">
              <span className="support-mini-tag">Submit a ticket</span>
              <h2>Tell us what you need help with.</h2>
              <p>
                Share the issue clearly so our team can route it to the right
                person. Include what you clicked, what you expected, and what
                happened.
              </p>

              <div className="support-assurance-list">
                <span>
                  <BadgeCheck size={17} /> Free/Starter: 1–2 business days
                </span>
                <span>
                  <BadgeCheck size={17} /> SME: within 24 hours
                </span>
                <span>
                  <BadgeCheck size={17} /> Business: priority response
                </span>
              </div>
            </div>

            <form className="support-ticket-form" onSubmit={submitTicket}>
              <div className="support-form-grid">
                <label>
                  Full name
                  <input
                    name="name"
                    required
                    value={ticket.name}
                    onChange={onTicketChange}
                    placeholder="Your name"
                  />
                </label>

                <label>
                  Email
                  <input
                    type="email"
                    name="email"
                    required
                    value={ticket.email}
                    onChange={onTicketChange}
                    placeholder="you@company.com"
                  />
                </label>
              </div>

              <div className="support-form-grid">
                <label>
                  Issue type
                  <select
                    name="type"
                    value={ticket.type}
                    onChange={onTicketChange}
                  >
                    <option>Billing</option>
                    <option>Getting Started</option>
                    <option>Sales & Invoicing</option>
                    <option>Accounting & Reports</option>
                    <option>Inventory</option>
                    <option>Payroll</option>
                    <option>Team & Roles</option>
                    <option>CRM</option>
                    <option>Booking & Scheduling</option>
                    <option>Budgeting / FP&A</option>
                    <option>Banking & Reconciliation</option>
                    <option>Fixed Assets</option>
                    <option>Technical</option>
                    <option>Other</option>
                  </select>
                </label>

                <label>
                  Priority
                  <select
                    name="priority"
                    value={ticket.priority}
                    onChange={onTicketChange}
                  >
                    <option>Low</option>
                    <option>Normal</option>
                    <option>High</option>
                    <option>Urgent</option>
                  </select>
                </label>
              </div>

              <label>
                Subject
                <input
                  name="subject"
                  required
                  value={ticket.subject}
                  onChange={onTicketChange}
                  placeholder="Short summary"
                />
              </label>

              <label>
                Message
                <textarea
                  name="message"
                  rows="5"
                  required
                  value={ticket.message}
                  onChange={onTicketChange}
                  placeholder="Describe the issue, steps to reproduce, and what you expected..."
                />
              </label>

              <div className="support-file-note">
                Tip: screenshots help us resolve issues faster. After
                submitting, our team can request screenshots if needed.
              </div>

              <button
                className="support-btn support-btn-primary"
                type="submit"
                disabled={sending}
              >
                {sending ? "Sending..." : "Submit ticket"}
              </button>

              {sent === "ok" && (
                <p className="form-success">
                  Thanks! Your ticket has been submitted.
                </p>
              )}

              {sent === "err" && (
                <p className="form-error">
                  Something went wrong. Please try again.
                </p>
              )}
            </form>
          </div>
        </section>

        <section className="support-section support-section-alt">
          <div className="kp-container">
            <div className="support-section-head">
              <span className="support-mini-tag">Common questions</span>
              <h2>Frequently asked questions</h2>
            </div>

            <div className="support-faq-grid">
              {faqs.map((faq) => (
                <div className="support-faq-item" key={faq.q}>
                  <h3>{faq.q}</h3>
                  <p>{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="kp-container support-section">
          <div className="support-assurance-grid">
            <AssuranceCard
              icon={ShieldCheck}
              title="Local, friendly support"
              text="We understand Nigerian SME workflows, VAT, WHT, payroll, and everyday business realities."
            />
            <AssuranceCard
              icon={WalletCards}
              title="Practical finance help"
              text="We help users understand what to click, what to record, and how to read their business reports."
            />
            <AssuranceCard
              icon={BarChart3}
              title="No lock-in"
              text="Your records and reports can be exported in common formats where available."
            />
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function SupportContactCard({
  icon: Icon,
  title,
  badge,
  text,
  actionText,
  href,
}) {
  return (
    <motion.div variants={fadeInUp} className="support-contact-card">
      <div className="support-contact-head">
        <div className="support-icon-wrap">
          <Icon size={22} />
        </div>
        {badge && <span className="support-badge">{badge}</span>}
      </div>
      <h3>{title}</h3>
      <p>{text}</p>
      <a className="support-btn support-btn-ghost" href={href}>
        {actionText}
      </a>
    </motion.div>
  );
}

function VideoCard({ title, videoId }) {
  return (
    <div className="support-video-card">
      <div className="support-video-frame">
        <iframe
          title={title}
          src={`https://www.youtube.com/embed/${videoId}?rel=0`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
        />
      </div>
      <h4>{title}</h4>
    </div>
  );
}

function AssuranceCard({ icon: Icon, title, text }) {
  return (
    <div className="support-assurance-card">
      <div className="support-icon-wrap soft">
        <Icon size={22} />
      </div>
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}
